import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { forkJoin, of, Subscription } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { Router } from "@angular/router";

import { EvaService } from "src/app/core/services/eva.service";
import { onDocumentClickCapture } from "src/app/shared/ds/utils/listen-outside-angular";
import { EvaConfirmService } from "src/app/shared/ds/eva-confirm-dialog/eva-confirm.service";
import { EmployeesService } from "src/app/features/employees/services/employees.service";
import { AssigneeItem } from "../task-assignee-picker/task-assignee-picker.component";

import { EVA_OBJECT_TAGS, TagPreset } from "src/app/shared/constants/tag-presets";
import { Task, TaskObjectLink, TaskStatus, TaskUrgency } from "../domains/task";
import { TaskCategory } from "../domains/task-category";
import { TaskLot } from "../domains/task-lot";
import { defaultAssigneeFor } from "../domains/default-assignee";
import { applyManualOrder, mergeManualOrder } from "../domains/manual-order";
import { EvaCompanyService } from "src/app/features/eva-company/services/evaCompany.service";
import { TaskService } from "../services/task.service";
import { TaskCategoryService } from "../services/task-category.service";
import { TaskLotService } from "../services/task-lot.service";
import { ProjectSectionConfigService } from "../services/project-section-config.service";
import { SectionItem } from "../domains/project-section-config";
import { TaskInlineCreateComponent } from "../task-inline-create/task-inline-create.component";

// Interface pour une section de tâches (Aujourd'hui / Plus tard / À faire / Terminées)
export interface TaskSection {
  key: string;
  title: string;
  tasks: Task[];
  collapsed: boolean;
  /** Identifiant CDK drop list */
  dropListId: string;
  /** IDs des listes connectées pour le drag & drop */
  connectedTo?: string[];
}

// Item affiché dans une section : tâche seule, lot ou groupe par objet lié
export type GroupSubItem = { kind: "task"; task: Task } | { kind: "lot"; lot: TaskLot; tasks: Task[] };
export type SectionDisplayItem =
  | { kind: "task"; task: Task }
  | { kind: "lot"; lot: TaskLot; tasks: Task[] }
  | { kind: "object-group"; link: TaskObjectLink; items: GroupSubItem[] };

const OBJECT_TYPE_TO_EVA: Record<string, string> = {
  CUSTOMER: "CLIENT",
  PROJECT: "CHANTIER",
  OFFER: "OFFRE",
  INVOICE: "FACTURE_CLIENT",
  EMPLOYEE: "COLLABORATEUR",
  SUPPLIER: "FOURNISSEUR",
  PURCHASE_ORDER: "BON_COMMANDE",
  SUPPLIER_INVOICE: "FACTURE_ACHAT",
  INTERVENTION: "INTERVENTION",
  CONTRACT: "CONTRAT",
  CUSTOMER_SITE: "SITE",
  CATALOG: "CATALOGUE",
  ARTICLE: "ARTICLE",
  EXPENSE_REPORT: "NOTE_FRAIS",
};

@Component({
  selector: "app-task-list-shell",
  templateUrl: "./task-list-shell.component.html",
  styleUrls: ["./task-list-shell.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaskListShellComponent implements OnInit, OnChanges {
  private destroyRef = inject(DestroyRef);
  private host = inject<ElementRef<HTMLElement>>(ElementRef);
  private confirmService = inject(EvaConfirmService);
  private evaCompanyService = inject(EvaCompanyService);

  // ==========================================================================
  // Inputs
  // ==========================================================================

  /** Contexte d'utilisation du composant */
  @Input({ required: true }) context: "drawer" | "object" | "standalone";

  /** Filtre objet lié (pour context='object') */
  @Input() objectFilter: { object_id: string; object_type: string; object_name?: string } | null = null;

  /** Contexte objet : sections affichées (le galet n'en montre qu'une selon son onglet) */
  @Input() objectSections: "both" | "active" | "done" = "both";

  /** État vide : lien « Voir toutes mes tâches » (galet), relié à showAllRequested */
  @Input() showAllLink = false;
  @Output() showAllRequested = new EventEmitter<void>();

  /** Mode compact (lignes de 36px au lieu de 44px) */
  @Input() compact = false;

  /** Afficher le formulaire de création inline */
  @Input() showInlineCreate = true;

  /** Afficher le trigger dans chaque section (si false, seul le formulaire est possible via openInlineCreate()) */
  @Input() showInlineCreateTrigger = true;

  /** Garder le formulaire inline ouvert après une création, pour enchaîner plusieurs tâches */
  @Input() inlineCreateStayOpen = false;

  /** Activer le drag & drop entre sections */
  @Input() enableDragDrop = false;

  /** Afficher le lien objet sur chaque tâche */
  @Input() showObjectLink = true;

  /** Afficher l'affectation (avatars) sur chaque tâche */
  @Input() showAssignees = true;

  /** Mode de vue contrôlé par le parent */
  @Input() viewMode: "all" | "mine" | "completed" | "all-flat" = "mine";

  /** Filtre par types de lien objet (tableau de types, null = tous) */
  @Input() linkTypeFilter: string[] | null = null;

  /** Tri des tâches */
  /** « manual » : ordre glissé à la main (façon Todoist), les tâches jamais déplacées restant triées par échéance */
  @Input() sortBy: "manual" | "date" | "urgency" | "title" = "date";

  /** Filtre contextuel drawer : objet métier courant (passe object_id + object_type au filterTasks API) */
  @Input() drawerObjectFilter: { object_id: string; object_type: string } | null = null;

  /** Recherche textuelle (passée au filterTasks API) */
  @Input() searchQuery = "";

  /** Filtre par employee_id de l'assigné */
  @Input() collaboratorFilter: string | null = null;

  /** Filtre par object_id d'un projet spécifique (chip Chantier) */
  @Input() projectIdFilter: string | null = null;

  /** Incrémenter pour forcer un rechargement depuis le parent */
  @Input() refreshTrigger = 0;

  // ==========================================================================
  // Outputs
  // ==========================================================================

  @Output() taskClicked = new EventEmitter<Task>();
  @Output() taskEditRequested = new EventEmitter<Task>();
  @Output() tasksCountChange = new EventEmitter<number>();
  /** Émet les tâches brutes (non filtrées) pour que le parent puisse construire les chips de filtre */
  @Output() rawTasksLoaded = new EventEmitter<Task[]>();
  @Output() addTaskForLotRequested = new EventEmitter<string>();
  @Output() lotsChanged = new EventEmitter<TaskLot[]>();

  // ==========================================================================
  // ViewChildren
  // ==========================================================================

  @ViewChildren(TaskInlineCreateComponent) inlineCreates: QueryList<TaskInlineCreateComponent>;

  // ==========================================================================
  // State
  // ==========================================================================

  loading = false;
  sections: TaskSection[] = [];
  employees: any[] = [];
  categories: TaskCategory[] = [];

  /** Cache des tâches brutes pour re-appliquer filtre/tri sans rappel API */
  private rawTasks: Task[] = [];

  /** ID de la tâche en cours d'édition inline (null = aucune) */
  editingTaskId: string | null = null;

  /** Assigné par défaut = utilisateur courant (chargé une seule fois) */
  currentUserAssignee: AssigneeItem | null = null;

  // ==========================================================================
  // State — Lots & sélection multiple
  // ==========================================================================

  lots: TaskLot[] = [];
  sectionOrder: SectionItem[] = [];
  /** Ordre manuel de la vue globale (« Toutes les tâches »), propre à chaque utilisateur */
  private userOrder: string[] = [];
  lotConnectionsMap = new Map<string, string[]>();

  /** ID du lot nouvellement créé — affiché même s'il est vide, réinitialisé au prochain loadTasks() */
  newLotId: string | null = null;
  selectionMode = false;
  selectedTaskIds = new Set<string>();
  contextMenuVisible = false;
  /** Tâche visée par le menu (clic droit ou bouton ⋮ de sa ligne) */
  contextMenuTask: Task | null = null;
  contextMenuX = 0;
  contextMenuY = 0;
  /** Masqué tant que la position n'est pas corrigée, pour éviter un saut visible */
  contextMenuPlaced = false;

  // ==========================================================================
  // Computed — liens objets par défaut pour l'inline create
  // ==========================================================================

  get defaultObjectLinks(): TaskObjectLink[] {
    if (!this.objectFilter) return [];
    return [
      {
        object_id: this.objectFilter.object_id,
        object_type: this.objectFilter.object_type,
        object_name: this.objectFilter.object_name,
      },
    ];
  }

  /** Aucune date pré-remplie par défaut — l'utilisateur choisit lui-même */
  /** « Ajouter une tâche » sous « Aujourd'hui » crée une tâche pour aujourd'hui (comme Todoist) */
  getDefaultDateForSection(sectionKey: string): Date | null {
    return sectionKey === "today" ? this.getStartOfDay(new Date()) : null;
  }

  /**
   * Retourne l'assigné par défaut selon le contexte :
   * - drawer / standalone → utilisateur courant (toutes sections)
   * - object → null (pas d'assigné automatique sur les fiches)
   */
  getDefaultAssigneeForSection(): AssigneeItem | null {
    if (this.context === "object") return null;
    return this.currentUserAssignee;
  }

  get hasTasks(): boolean {
    return this.sections.some((s) => s.tasks.length > 0) || this.newLotId !== null || this.lots.length > 0;
  }

  /** Titre de l'état vide, comme les notes : « Aucune tâche pour Offre n°… » sur une fiche */
  get emptyTitle(): string {
    const name = this.context === "object" ? this.objectFilter?.object_name?.trim() : "";
    if (this.isDoneView) return name ? `Aucune tâche terminée pour ${name}` : "Aucune tâche terminée";
    if (name && this.isMineView) return `Aucune tâche pour vous sur ${name}`;
    return name ? `Aucune tâche pour ${name}` : "Aucune tâche";
  }

  /** Onglet « Terminées », en portée globale comme sur une fiche */
  get isDoneView(): boolean {
    return this.viewMode === "completed" || (this.context === "object" && this.objectSections === "done");
  }

  /** Filtré sur le collaborateur connecté (onglet « Mes tâches » sur une fiche) */
  private get isMineView(): boolean {
    const me = this.evaCompanyService.getCurrentEvaEmployee()?.employee_id;
    return !!me && this.collaboratorFilter === me;
  }

  /** Liste vide à cause de la recherche ou du filtre collaborateur, pas faute de tâches */
  get isFiltered(): boolean {
    // Filtrer sur soi-même (« Mes tâches ») n'est pas une recherche : état vide normal
    const me = this.evaCompanyService.getCurrentEvaEmployee()?.employee_id;
    return !!this.searchQuery?.trim() || (!!this.collaboratorFilter && this.collaboratorFilter !== me);
  }

  /** Création demandée depuis l'état vide : les sections s'affichent pour accueillir le formulaire */
  createRequested = false;

  /** Ouvre le formulaire de création, même quand la liste est vide (bouton de l'état vide, « + Tâche ») */
  startCreate(): void {
    this.createRequested = true;
    this.cdr.markForCheck();
    setTimeout(() => this.openInlineCreate());
  }

  get activeDropListIds(): string[] {
    return this.sections.map((s) => s.dropListId);
  }

  getLotDropListIds(): string[] {
    return this.lots.map((l) => `lot-drop-${l.lot_id}`);
  }

  // ==========================================================================
  // Lifecycle
  // ==========================================================================

  constructor(
    private cdr: ChangeDetectorRef,
    private taskService: TaskService,
    private taskCategoryService: TaskCategoryService,
    private employeesService: EmployeesService,
    public evaService: EvaService,
    private router: Router,
    private taskLotService: TaskLotService,
    private sectionConfigService: ProjectSectionConfigService,
  ) {}

  ngOnInit(): void {
    this.loadTasks();
    this.loadEmployees();
    this.loadCategories();
    this.loadCurrentUserAssignee();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Rechargement API si le viewMode, le filtre objet, le filtre drawer ou la recherche changent
    if (
      (changes["viewMode"] && !changes["viewMode"].firstChange) ||
      (changes["objectFilter"] && !changes["objectFilter"].firstChange) ||
      (changes["drawerObjectFilter"] && !changes["drawerObjectFilter"].firstChange) ||
      (changes["searchQuery"] && !changes["searchQuery"].firstChange) ||
      (changes["refreshTrigger"] && !changes["refreshTrigger"].firstChange)
    ) {
      this.loadTasks();
    }
    // Re-filtrage/tri local si linkTypeFilter, sortBy, collaboratorFilter ou projectIdFilter changent
    if (
      (changes["linkTypeFilter"] && !changes["linkTypeFilter"].firstChange) ||
      (changes["sortBy"] && !changes["sortBy"].firstChange) ||
      (changes["collaboratorFilter"] && !changes["collaboratorFilter"].firstChange) ||
      (changes["projectIdFilter"] && !changes["projectIdFilter"].firstChange) ||
      (changes["objectSections"] && !changes["objectSections"].firstChange)
    ) {
      if (this.context === "object") {
        // En contexte object, on re-construit les sections (pas le format drawer today/later)
        this.buildObjectSections(this.rawTasks);
      } else {
        this.applyFilterAndSort();
      }
    }
  }

  // ==========================================================================
  // Méthodes publiques
  // ==========================================================================

  /** Forcer le rechargement depuis le parent */
  refresh(): void {
    this.loadTasks();
  }

  /** Ouvre le formulaire inline create de la première section non-done */
  openInlineCreate(): void {
    const first = this.inlineCreates?.first;
    if (first) {
      first.open();
    }
  }

  /** Crée un lot vide et l'affiche immédiatement en mode édition */
  createEmptyLot(): void {
    const projectId = this.objectFilter?.object_id;
    if (!projectId) return;
    this.taskLotService
      .create("Nouveau lot", projectId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (lot) => {
          this.lots = [...this.lots, lot];
          this.newLotId = lot.lot_id;
          if (this.context === "object") {
            this.buildObjectSections(this.rawTasks);
          } else {
            this.applyFilterAndSort();
          }
          this.cdr.markForCheck();
        },
      });
  }

  /** Affiche un lot déjà créé (via dialog) sans focus rename — le nom est déjà défini */
  showCreatedLot(lot: TaskLot): void {
    this.lots = [...this.lots.filter((l) => l.lot_id !== lot.lot_id), lot];
    if (this.context === "object") {
      this.buildObjectSections(this.rawTasks);
    } else {
      this.applyFilterAndSort();
    }
    this.cdr.markForCheck();
  }

  // ==========================================================================
  // Chargement des données
  // ==========================================================================

  private loadTasksSub?: Subscription;

  /** silent : recharge sans squelette, pour ne pas détruire le formulaire inline ouvert */
  private loadTasks(silent = false): void {
    // Une réponse tardive (autre objet, rechargement précédent) ne doit pas écraser la liste
    this.loadTasksSub?.unsubscribe();
    if (!silent) {
      this.loading = true;
      this.createRequested = false;
    }
    this.newLotId = null;
    this.cdr.markForCheck();

    if (this.context === "object" && this.objectFilter) {
      // object_type normalisé en majuscules côté back (EP-1120) : la route par objet suffit,
      // et elle montre toutes les tâches non privées de l'objet, pas seulement celles de l'utilisateur
      const objectId = this.objectFilter.object_id;
      const objectTypeUpper = this.objectFilter.object_type.toUpperCase();
      const tasks$ = this.taskService
        .getTasksByObject(objectTypeUpper, objectId)
        .pipe(map((tasks) => ({ tasks: tasks.filter((t) => t.status !== TaskStatus.CANCELLED) })));
      // Les lots sont rattachés à l'identifiant de l'objet, quel qu'en soit le type (chantier, offre…)
      const lots$ = this.taskLotService.getByProject(objectId);
      // Ordre manuel des tâches, rangé par identifiant d'objet (chantier, offre…) malgré le nom « project »
      const sectionConfig$ =
        this.viewMode === "all-flat" || this.enableDragDrop
          ? this.sectionConfigService.getByProject(objectId)
          : of(null);
      this.loadTasksSub = forkJoin([tasks$, lots$, sectionConfig$])
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: ([response, lots, sectionConfig]: [any, TaskLot[], any]) => {
            this.lots = lots;
            this.sectionOrder = sectionConfig?.section_order ?? [];
            const allTasks: Task[] = response.tasks || [];
            const tasks = allTasks.filter((t) =>
              t.objects_links?.some(
                (l) => l.object_type?.toUpperCase() === objectTypeUpper && l.object_id === objectId,
              ),
            );
            this.buildObjectSections(tasks);
            this.loading = false;
            this.cdr.markForCheck();
          },
          error: () => {
            this.evaService.showError("Impossible de charger les tâches", "loadTasks", "");
            this.loading = false;
            this.cdr.markForCheck();
          },
        });
    } else {
      // Contexte drawer ou standalone : utilise filterTasks
      const statusFilter =
        this.viewMode === "completed"
          ? [TaskStatus.DONE, TaskStatus.CANCELLED]
          : [TaskStatus.TODO, TaskStatus.IN_PROGRESS];

      const filterParams: any = {
        view_mode: this.viewMode === "completed" ? "mine" : this.viewMode,
        status: statusFilter,
        page: 1,
        page_size: 500,
      };

      // Filtre contextuel objet (drawer)
      if (this.drawerObjectFilter) {
        filterParams.object_id = this.drawerObjectFilter.object_id;
        filterParams.object_type = this.drawerObjectFilter.object_type;
      }

      // Recherche textuelle
      if (this.searchQuery) {
        filterParams.search = this.searchQuery;
      }

      const userOrderKey = this.userOrderKey;
      const userOrder$ = userOrderKey ? this.sectionConfigService.getByProject(userOrderKey) : of(null);
      this.loadTasksSub = forkJoin([this.taskService.filterTasks(filterParams), userOrder$])
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: ([response, userOrder]: [any, any]) => {
            const tasks: Task[] = response.tasks || [];
            this.userOrder = (userOrder?.section_order ?? []).map((item: SectionItem) => item.id);
            this.buildDrawerSections(tasks);
            this.loading = false;
            this.cdr.markForCheck();
          },
          error: () => {
            this.evaService.showError("Impossible de charger les tâches", "loadTasks", "");
            this.loading = false;
            this.cdr.markForCheck();
          },
        });
    }
  }

  private loadEmployees(): void {
    this.employeesService
      .getAllEnabledEmployees()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((employees) => {
        this.employees = employees;
        this.cdr.markForCheck();
      });
  }

  private loadCurrentUserAssignee(): void {
    this.employeesService
      .getCurrentEmployee()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((employee) => {
        this.currentUserAssignee = defaultAssigneeFor(
          employee,
          this.evaCompanyService.getCurrentEvaCompany()?.eva_company_id,
        );
        this.cdr.markForCheck();
      });
  }

  private loadCategories(): void {
    this.taskCategoryService
      .getCategories()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((categories) => {
        this.categories = categories;
        this.cdr.markForCheck();
      });
  }

  // ==========================================================================
  // Construction des sections
  // ==========================================================================

  /** Sections pour le contexte drawer : Aujourd'hui / Plus tard */
  private buildDrawerSections(tasks: Task[]): void {
    this.rawTasks = tasks;
    this.rawTasksLoaded.emit(tasks);
    this.applyFilterAndSort();
  }

  /** Applique filtre + tri sur rawTasks puis reconstruit les sections (sans rappel API) */
  applyFilterAndSort(): void {
    let tasks = [...this.rawTasks];

    // Filtre par types de lien
    if (this.linkTypeFilter && this.linkTypeFilter.length > 0) {
      const knownTypes = ["PROJECT", "INTERVENTION", "OFFER", "EXPENSE_REPORT", "INVOICE"];
      if (this.linkTypeFilter.includes("NONE")) {
        // "Autre" : tâches sans lien vers un type connu
        tasks = tasks.filter((t) => !t.objects_links?.some((l) => knownTypes.includes(l.object_type?.toUpperCase())));
      } else {
        tasks = tasks.filter((t) =>
          t.objects_links?.some((l) => this.linkTypeFilter!.includes(l.object_type?.toUpperCase())),
        );
      }
    }

    // Filtre par collaborateur
    if (this.collaboratorFilter) {
      tasks = tasks.filter((t) => t.assigned_to?.some((a) => a.employee_id === this.collaboratorFilter));
    }

    // Filtre par projet spécifique (chip Chantier + second filtre)
    if (this.projectIdFilter) {
      tasks = tasks.filter((t) =>
        t.objects_links?.some(
          (l) => l.object_type?.toUpperCase() === "PROJECT" && l.object_id === this.projectIdFilter,
        ),
      );
    }

    // Terminées : une seule liste sans en-tête, la plus récemment terminée en tête,
    // pour ne pas ranger une tâche finie sous « En retard »
    if (this.viewMode === "completed") {
      tasks = this.sortByCompletion(tasks);
      this.sections = [{ key: "done", title: "", tasks, collapsed: false, dropListId: "shell-done-list" }];
      this.emitCount(tasks);
      this.cdr.markForCheck();
      return;
    }

    // À faire / Mes tâches : une tâche cochée quitte la liste tout de suite (« Annuler » la ramène)
    tasks = tasks.filter((t) => t.status === TaskStatus.TODO || t.status === TaskStatus.IN_PROGRESS);

    // Tri
    tasks = this.applySortBy(tasks);

    const today = this.getStartOfDay(new Date());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    // En retard séparé d'« Aujourd'hui » et affiché en tête seulement s'il y en a (EP-1120)
    let overdueTasks = tasks.filter((t) => t.due_date && this.getStartOfDay(t.due_date).getTime() < today.getTime());
    let todayTasks = tasks.filter((t) => t.due_date && this.getStartOfDay(t.due_date).getTime() === today.getTime());
    let laterTasks = tasks.filter((t) => !t.due_date || this.getStartOfDay(t.due_date) >= tomorrow);
    if (this.sortBy === "manual") {
      overdueTasks = applyManualOrder(overdueTasks, this.userOrder);
      todayTasks = applyManualOrder(todayTasks, this.userOrder);
      laterTasks = applyManualOrder(laterTasks, this.userOrder);
    }

    this.sections = [
      ...(overdueTasks.length
        ? [
            {
              key: "overdue",
              title: "En retard",
              tasks: overdueTasks,
              collapsed: false,
              dropListId: "shell-overdue-list",
              connectedTo: ["shell-today-list", "shell-later-list"],
            },
          ]
        : []),
      {
        key: "today",
        title: "Aujourd'hui",
        tasks: todayTasks,
        collapsed: false,
        dropListId: "shell-today-list",
        connectedTo: ["shell-later-list"],
      },
      {
        key: "later",
        title: "Plus tard",
        tasks: laterTasks,
        collapsed: false,
        dropListId: "shell-later-list",
        connectedTo: ["shell-today-list"],
      },
    ];

    this.emitCount(tasks);
    this.cdr.markForCheck();
  }

  /** Tâches terminées : la plus récemment terminée en tête, quel que soit le tri choisi */
  private sortByCompletion(tasks: Task[]): Task[] {
    return [...tasks].sort((a, b) => new Date(b.completed_at ?? 0).getTime() - new Date(a.completed_at ?? 0).getTime());
  }

  private applySortBy(tasks: Task[]): Task[] {
    return [...tasks].sort((a, b) => {
      switch (this.sortBy) {
        case "urgency": {
          const order: Record<string, number> = {
            [TaskUrgency.URGENT]: 0,
            [TaskUrgency.NORMAL]: 1,
            [TaskUrgency.LOW]: 2,
          };
          return (order[a.urgency] ?? 1) - (order[b.urgency] ?? 1);
        }
        case "title":
          return (a.title || "").localeCompare(b.title || "", "fr");
        case "date":
        default:
          if (!a.due_date && !b.due_date) return 0;
          if (!a.due_date) return 1;
          if (!b.due_date) return -1;
          return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
      }
    });
  }

  /** Sections pour le contexte object : À faire / Terminées */
  private buildObjectSections(allTasks: Task[]): void {
    this.rawTasks = allTasks;
    const tasks = this.filterObjectTasks(allTasks);

    if (this.viewMode === "all-flat") {
      const sorted = this.applySortBy(tasks);
      const lotIds = this.lots.map((l) => `lot-drop-${l.lot_id}`);
      this.sections = [
        {
          key: "all",
          title: "",
          tasks: sorted,
          collapsed: false,
          dropListId: "shell-all-flat-list",
          connectedTo: lotIds,
        },
      ];
      this.buildLotConnections();
      this.rawTasksLoaded.emit(allTasks);
      this.emitCount(tasks);
      this.cdr.markForCheck();
      return;
    }

    // Même ordre que la vue globale : échéance la plus proche d'abord (retards en tête), sans date à la fin
    const activeTasks = this.applySortBy(
      tasks.filter((t) => t.status === TaskStatus.TODO || t.status === TaskStatus.IN_PROGRESS),
    );
    const doneTasks = this.sortByCompletion(
      tasks.filter((t) => t.status === TaskStatus.DONE || t.status === TaskStatus.CANCELLED),
    );

    this.sections = [
      {
        key: "active",
        title: "À faire",
        tasks: activeTasks,
        collapsed: false,
        dropListId: "shell-active-list",
      },
      {
        key: "done",
        title: "Terminées",
        tasks: doneTasks,
        // Seule section affichée (onglet Terminées du galet) : dépliée
        collapsed: this.objectSections !== "done",
        dropListId: "shell-done-list",
      },
    ].filter((s) => this.objectSections === "both" || s.key === this.objectSections);
    this.buildLotConnections();
    this.rawTasksLoaded.emit(allTasks);
    this.emitCount(activeTasks);
  }

  /** Recherche et filtre collaborateur de la barre d'outils, appliqués aux tâches d'un objet */
  private filterObjectTasks(tasks: Task[]): Task[] {
    const term = (this.searchQuery || "").trim().toLowerCase();
    return tasks.filter(
      (t) =>
        (!term || `${t.title || ""} ${t.description || ""}`.toLowerCase().includes(term)) &&
        (!this.collaboratorFilter || t.assigned_to?.some((a) => a.employee_id === this.collaboratorFilter)),
    );
  }

  private emitCount(tasks: Task[]): void {
    const active = tasks.filter((t) => t.status === TaskStatus.TODO || t.status === TaskStatus.IN_PROGRESS).length;
    this.tasksCountChange.emit(active);
  }

  // ==========================================================================
  // Interactions tâches
  // ==========================================================================

  onStatusChange(event: { task: Task; done: boolean; status?: TaskStatus }): void {
    const newStatus = event.status ?? (event.done ? TaskStatus.DONE : TaskStatus.TODO);
    const found = this.rawTasks.find((t) => t.task_id === event.task.task_id);
    const previousStatus = found?.status ?? event.task.status;

    // Mise à jour optimiste — évite le rechargement complet et le saut d'écran
    if (found) found.status = newStatus;
    if (this.context === "object") {
      this.buildObjectSections(this.rawTasks);
    } else {
      this.applyFilterAndSort();
    }
    this.cdr.markForCheck();

    this.taskService
      .updateTaskStatus(event.task.task_id, newStatus)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          // Terminer d'un clic, annuler d'un clic (EP-1120)
          if (newStatus === TaskStatus.DONE && previousStatus !== TaskStatus.DONE) {
            this.evaService.showUndo("Tâche terminée", () =>
              this.onStatusChange({ task: event.task, done: false, status: previousStatus }),
            );
          }
        },
        error: () => {
          this.evaService.showError("Impossible de mettre à jour la tâche", "updateTaskStatus", "");
          this.loadTasks(); // rollback
        },
      });
  }

  onTaskClick(task: Task): void {
    this.taskEditRequested.emit(task);
  }

  onTaskEdited(task: Task): void {
    this.editingTaskId = null;
    this.taskClicked.emit(task);
    this.loadTasks();
  }

  onEditCancelled(): void {
    this.editingTaskId = null;
    this.cdr.markForCheck();
  }

  onTaskCreated(task: Task): void {
    this.loadTasks(true);
  }

  onTaskDeleted(): void {
    this.editingTaskId = null;
    this.loadTasks();
  }

  // ==========================================================================
  // Gestion des lots — sélection, menu contextuel, CRUD
  // ==========================================================================

  @HostListener("document:keydown", ["$event"])
  onKeyDown(event: KeyboardEvent): void {
    // Maj pour une majuscule dans un champ ne doit pas basculer en mode sélection
    const target = event.target;
    const typing = target instanceof Element && !!target.closest("input, textarea, [contenteditable='true']");
    if (event.key === "Shift" && !typing) {
      this.selectionMode = true;
      this.cdr.markForCheck();
    }
    if (event.key === "Escape") {
      this.clearSelection();
      this.contextMenuVisible = false;
      this.cdr.markForCheck();
    }
  }

  @HostListener("document:keyup", ["$event"])
  onKeyUp(event: KeyboardEvent): void {
    if (event.key === "Shift") {
      // Conserver le mode sélection si des tâches sont déjà sélectionnées
      this.selectionMode = this.selectedTaskIds.size > 0;
      this.cdr.markForCheck();
    }
  }

  // Clic (ou clic droit) à l'extérieur, en capture : le galet arrête la propagation des clics
  private readonly menuOrSelectionOpen = () => this.contextMenuVisible || this.selectedTaskIds.size > 0;
  private readonly stopOutsideClick = onDocumentClickCapture(this.menuOrSelectionOpen, (event) =>
    this.onDocumentClick(event),
  );
  private readonly stopOutsideContextMenu = onDocumentClickCapture(
    this.menuOrSelectionOpen,
    (event) => this.onDocumentClick(event),
    "contextmenu",
  );

  onDocumentClick(event: MouseEvent): void {
    // Le menu gère ses propres clics, et « Créer un lot » a besoin de la sélection
    // Le bouton ⋮ ouvre et ferme lui-même le menu (voir onTaskContextMenu)
    if ((event.target as HTMLElement | null)?.closest(".task-shell__context-menu, .task-row__menu-btn")) return;
    if (this.contextMenuVisible) {
      this.contextMenuVisible = false;
      this.cdr.markForCheck();
    }
    // Un clic simple hors des tâches annule la sélection multiple
    const onTask = (event.target as HTMLElement | null)?.closest(".task-row");
    if (event.type === "click" && this.selectedTaskIds.size > 0 && !event.shiftKey && !onTask) {
      this.clearSelection();
    }
  }

  onTaskSelectionToggle(task: Task): void {
    if (this.selectedTaskIds.has(task.task_id)) {
      this.selectedTaskIds.delete(task.task_id);
    } else {
      this.selectedTaskIds.add(task.task_id);
    }
    this.selectionMode = this.selectedTaskIds.size > 0;
    this.cdr.markForCheck();
  }

  /**
   * Menu d'une tâche, le même au clic droit et au bouton ⋮ de la ligne.
   * `alignRight` : ouvert depuis le bouton ⋮, le menu s'aligne sur son bord droit (x).
   */
  onTaskContextMenu(event: { task: Task; x: number; y: number; alignRight?: boolean }): void {
    // Second clic sur le ⋮ de la même tâche : on referme
    if (event.alignRight && this.contextMenuVisible && this.contextMenuTask?.task_id === event.task.task_id) {
      this.contextMenuVisible = false;
      this.cdr.markForCheck();
      return;
    }
    this.contextMenuTask = event.task;
    // Clic droit : inclure la tâche dans la sélection (le ⋮ ne touche pas à la sélection)
    if (!event.alignRight && !this.selectedTaskIds.has(event.task.task_id)) {
      this.selectedTaskIds.add(event.task.task_id);
      this.selectionMode = true;
    }
    this.contextMenuX = event.x;
    this.contextMenuY = event.y;
    this.contextMenuVisible = true;
    this.contextMenuPlaced = false;
    this.cdr.markForCheck();
    // Dans un panneau animé (transform), position: fixed part du panneau et non de l'écran :
    // on mesure le décalage réel et on le compense pour ouvrir le menu sous le curseur
    setTimeout(() => {
      const menu = this.host.nativeElement.querySelector<HTMLElement>(".task-shell__context-menu");
      if (!menu) return;
      const rect = menu.getBoundingClientRect();
      // Sous le curseur, en restant dans l'écran (près du bord droit, il s'ouvre à gauche)
      const left = Math.max(
        8,
        Math.min(event.alignRight ? event.x - rect.width : event.x, window.innerWidth - rect.width - 8),
      );
      const top = Math.min(event.y, window.innerHeight - rect.height - 8);
      this.contextMenuX += left - rect.left;
      this.contextMenuY += top - rect.top;
      this.contextMenuPlaced = true;
      this.cdr.markForCheck();
    });
  }

  /** Objet qui porte les lots (chantier, offre…) : les lots n'existent que dans la liste d'un objet. */
  get lotContainerId(): string | null {
    return this.context === "object" ? (this.objectFilter?.object_id ?? null) : null;
  }

  get canCreateLot(): boolean {
    return !!this.lotContainerId && this.selectedTaskIds.size >= 2;
  }

  createLot(): void {
    const taskIds = [...this.selectedTaskIds];

    // Règle : toutes les tâches d'un lot doivent être liées au même élément
    const selectedTasks = this.rawTasks.filter((t) => taskIds.includes(t.task_id));
    const tasksWithLinks = selectedTasks.filter((t) => t.objects_links && t.objects_links.length > 0);
    if (tasksWithLinks.length > 1) {
      const firstLinks = new Set(tasksWithLinks[0].objects_links.map((l) => `${l.object_type}:${l.object_id}`));
      const hasCommonElement = tasksWithLinks
        .slice(1)
        .every((t) => t.objects_links.some((l) => firstLinks.has(`${l.object_type}:${l.object_id}`)));
      if (!hasCommonElement) {
        this.evaService.showError("Un lot ne peut contenir que des tâches liées au même élément.", "createLot", "");
        this.contextMenuVisible = false;
        this.cdr.markForCheck();
        return;
      }
    }

    const containerId = this.lotContainerId;
    if (!containerId) return;
    const fail = () => {
      this.evaService.showError("Le lot n'a pas pu être créé.", "createLot", "");
      this.contextMenuVisible = false;
      this.cdr.markForCheck();
    };
    this.taskLotService
      .create("Nouveau lot", containerId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (lot) => {
          const addCalls = taskIds.map((tid) => this.taskLotService.addTaskToLot(lot.lot_id, tid));
          (addCalls.length > 0 ? forkJoin(addCalls) : of([lot])).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: (results) => {
              const updatedLot = Array.isArray(results) && results.length > 0 ? results[results.length - 1] : lot;
              // Une tâche n'appartient qu'à un lot : le back la retire des autres, on fait de même ici
              const moved = new Set(taskIds);
              this.lots = [
                ...this.lots
                  .filter((l) => l.lot_id !== updatedLot.lot_id)
                  .map((l) => ({ ...l, task_ids: l.task_ids.filter((id) => !moved.has(id)) })),
                updatedLot,
              ];
              this.newLotId = updatedLot.lot_id;
              if (this.context === "object") {
                this.buildObjectSections(this.rawTasks);
              } else {
                this.applyFilterAndSort();
              }
              this.clearSelection();
              this.contextMenuVisible = false;
              this.cdr.markForCheck();
            },
            error: fail,
          });
        },
        error: fail,
      });
  }

  clearSelection(): void {
    this.selectedTaskIds = new Set<string>();
    this.selectionMode = false;
    this.cdr.markForCheck();
  }

  onLotRenamed(event: { lotId: string; title: string }): void {
    this.lots = this.lots.map((l) => (l.lot_id === event.lotId ? { ...l, title: event.title } : l));
    this.lotsChanged.emit(this.lots);
    this.cdr.markForCheck();
  }

  onLotDeleted(lotId: string): void {
    this.lots = this.lots.filter((l) => l.lot_id !== lotId);
    this.lotsChanged.emit(this.lots);
    if (this.context === "object") {
      this.buildObjectSections(this.rawTasks);
    } else {
      this.applyFilterAndSort();
    }
    this.cdr.markForCheck();
  }

  onTaskRemovedFromLot(taskId: string): void {
    this.lots = this.lots.map((l) => ({ ...l, task_ids: l.task_ids.filter((id) => id !== taskId) }));
    // Ajouter la tâche à sectionOrder (à la fin) pour qu'elle persiste au refresh
    if (this.hasManualOrder) {
      if (!this.sectionOrder.some((i) => i.type === "task" && i.id === taskId)) {
        const newOrder = this.sectionOrder.filter((i) => i.type === "lot" || i.id !== taskId);
        // Assurer que tous les lots sont présents
        const lotsInOrder = new Set(newOrder.filter((i) => i.type === "lot").map((i) => i.id));
        for (const lot of this.lots) {
          if (!lotsInOrder.has(lot.lot_id)) newOrder.push({ type: "lot", id: lot.lot_id });
        }
        newOrder.push({ type: "task", id: taskId });
        this.sectionOrder = newOrder;
        this.saveSectionOrder();
      }
    }
    if (this.context === "object") {
      this.buildObjectSections(this.rawTasks);
    } else {
      this.applyFilterAndSort();
    }
    this.cdr.markForCheck();
  }

  onAddTaskForLot(lotId: string): void {
    this.addTaskForLotRequested.emit(lotId);
  }

  onMarkAllDone(taskIds: string[]): void {
    if (taskIds.length === 0) return;
    forkJoin(taskIds.map((id) => this.taskService.updateTaskStatus(id, TaskStatus.DONE)))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: () => this.loadTasks() });
  }

  private buildLotConnections(): void {
    const map = new Map<string, string[]>();
    for (const lot of this.lots) {
      const otherLotIds = this.lots.filter((l) => l.lot_id !== lot.lot_id).map((l) => `lot-drop-${l.lot_id}`);
      map.set(lot.lot_id, [...otherLotIds, "shell-all-flat-list"]);
    }
    this.lotConnectionsMap = map;
  }

  getLotConnections(lot: TaskLot): string[] {
    return this.lotConnectionsMap.get(lot.lot_id) ?? [];
  }

  /**
   * Ordre manuel (glisser-déposer) mémorisé par objet : vue à plat d'un chantier, ou liste réordonnable
   * d'un objet (onglet Suivi d'une offre, tiroir). Sans ordre enregistré, le tri par échéance s'applique.
   */
  get hasManualOrder(): boolean {
    return (
      this.context === "object" && (this.viewMode === "all-flat" || (this.enableDragDrop && this.sortBy === "manual"))
    );
  }

  /** On ne glisse que là où l'ordre manuel a un sens (vue à plat d'un chantier, ou tri « Manuel ») */
  get canDragReorder(): boolean {
    return this.viewMode === "all-flat" || this.sortBy === "manual";
  }

  /** Clé de l'ordre manuel de la vue globale, rangée avec les ordres par objet (cloisonnés par entreprise) */
  private get userOrderKey(): string | null {
    const employeeId = this.evaCompanyService.getCurrentEvaEmployee()?.employee_id;
    return this.context !== "object" && this.enableDragDrop && this.viewMode !== "completed" && employeeId
      ? `tasks:${employeeId}`
      : null;
  }

  /** Enregistre l'ordre de la section après un glisser dans la vue globale */
  private saveUserOrder(section: TaskSection): void {
    const key = this.userOrderKey;
    if (!key || this.sortBy !== "manual") return;
    const known = new Set(this.rawTasks.map((t) => t.task_id));
    this.userOrder = mergeManualOrder(
      section.tasks.map((t) => t.task_id),
      this.userOrder.filter((id) => known.has(id)),
    );
    this.sectionConfigService
      .save(
        key,
        this.userOrder.map((id) => ({ type: "task" as const, id })),
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  /** Calcule l'ordre global depuis les items affichés dans la section all-flat */
  private computeSectionOrderFromItems(items: SectionDisplayItem[]): SectionItem[] {
    return items
      .filter((i) => i.kind === "lot" || i.kind === "task")
      .map((i) =>
        i.kind === "lot"
          ? { type: "lot" as const, id: i.lot.lot_id }
          : { type: "task" as const, id: (i as { kind: "task"; task: Task }).task.task_id },
      );
  }

  /** Persiste sectionOrder en base */
  private saveSectionOrder(): void {
    if (!this.hasManualOrder || !this.objectFilter?.object_id) return;
    this.sectionConfigService
      .save(this.objectFilter.object_id, this.sectionOrder)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  onTasksReordered(event: { lotId: string; taskIds: string[] }): void {
    // Mettre à jour this.lots pour que getLotTasksOrdered reflète le nouvel ordre
    this.lots = this.lots.map((l) => (l.lot_id === event.lotId ? { ...l, task_ids: event.taskIds } : l));
    if (this.context === "object") {
      this.buildObjectSections(this.rawTasks);
    } else {
      this.applyFilterAndSort();
    }
    this.cdr.markForCheck();
  }

  onMoveToLot(event: { task: Task; targetLotId: string }): void {
    this.taskLotService
      .addTaskToLot(event.targetLotId, event.task.task_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updatedLot) => {
          this.lots = this.lots.map((l) => {
            if (l.lot_id === updatedLot.lot_id) return updatedLot;
            return { ...l, task_ids: l.task_ids.filter((id) => id !== event.task.task_id) };
          });
          if (this.context === "object") {
            this.buildObjectSections(this.rawTasks);
          } else {
            this.applyFilterAndSort();
          }
          this.cdr.markForCheck();
        },
      });
  }

  /** Modifier / Supprimer depuis le menu de la tâche */
  onContextMenuAction(action: "edit" | "delete"): void {
    const task = this.contextMenuTask;
    this.contextMenuVisible = false;
    this.clearSelection();
    if (task) this.onMenuAction({ task, action });
  }

  onMenuAction(event: { task: Task; action: string }): void {
    if (event.action === "edit") {
      this.taskEditRequested.emit(event.task);
    } else if (event.action === "delete") {
      // Même confirmation que depuis le détail : la suppression est définitive
      this.confirmService.confirm({
        message: "Êtes-vous sûr de vouloir supprimer cette tâche ?",
        header: "Confirmation de suppression",
        acceptLabel: "Supprimer",
        rejectLabel: "Annuler",
        severity: "danger",
        accept: () =>
          this.taskService
            .deleteTask(event.task.task_id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                this.evaService.showSuccess("Tâche supprimée");
                this.loadTasks();
              },
            }),
      });
    }
  }

  onDropOnLot(event: { task: Task; lotId: string; index: number }): void {
    this.taskLotService
      .addTaskToLot(event.lotId, event.task.task_id)
      .pipe(
        // Réordonner pour insérer la tâche à la position de drop (et non en fin)
        switchMap((updatedLot) => {
          const ids = updatedLot.task_ids.filter((id) => id !== event.task.task_id);
          ids.splice(Math.min(event.index, ids.length), 0, event.task.task_id);
          return this.taskLotService.update(event.lotId, { task_ids: ids });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (updatedLot) => {
          // Mettre à jour le lot cible et retirer la tâche des autres lots
          this.lots = this.lots.map((l) => {
            if (l.lot_id === updatedLot.lot_id) return updatedLot;
            return { ...l, task_ids: l.task_ids.filter((id) => id !== event.task.task_id) };
          });
          // Retirer la tâche de sectionOrder (elle fait maintenant partie d'un lot)
          if (this.hasManualOrder) {
            this.sectionOrder = this.sectionOrder.filter((i) => !(i.type === "task" && i.id === event.task.task_id));
            this.saveSectionOrder();
          }
          if (this.context === "object") {
            this.buildObjectSections(this.rawTasks);
          } else {
            this.applyFilterAndSort();
          }
          this.cdr.markForCheck();
        },
      });
  }

  getSectionDisplayItems(section: TaskSection): SectionDisplayItem[] {
    const items: SectionDisplayItem[] = [];
    const renderedTaskIds = new Set<string>();
    const renderedLotIds = new Set<string>();

    // Retourne les tâches d'un lot dans l'ordre persisté (lot.task_ids), pas l'ordre du sort global
    const getLotTasksOrdered = (lot: TaskLot): Task[] => {
      const idIndex = new Map(lot.task_ids.map((id, i) => [id, i]));
      return section.tasks
        .filter((t) => idIndex.has(t.task_id))
        .sort((a, b) => (idIndex.get(a.task_id) ?? 0) - (idIndex.get(b.task_id) ?? 0));
    };

    const lk = (type: string, id: string) => `${type?.toUpperCase()}:${id}`;

    // Lien commun d'un lot — résolution par object_id exact uniquement
    const getLotLink = (lot: TaskLot): TaskObjectLink | null => {
      const lotTasks = getLotTasksOrdered(lot);
      const withLinks = lotTasks.filter((t) => t.objects_links && t.objects_links.length > 0);
      if (withLinks.length === 0) return null;
      for (const link of withLinks[0].objects_links) {
        const key = lk(link.object_type, link.object_id);
        if (!withLinks.every((t) => t.objects_links.some((l) => lk(l.object_type, l.object_id) === key))) continue;
        if (link.object_name) return link;
        // Même object_id, chercher le nom dans les tâches standalone
        for (const st of section.tasks) {
          if (lot.task_ids.includes(st.task_id)) continue;
          const named = st.objects_links?.find((l) => lk(l.object_type, l.object_id) === key && l.object_name);
          if (named) return named;
        }
        // Pas de fallback par type seul — évite de rattacher un lot au mauvais groupe
      }
      return null;
    };

    const objectGroupMap = new Map<string, { link: TaskObjectLink; items: GroupSubItem[] }>();
    // Liste réordonnable (tiroir) : à plat comme dans un objet, chaque ligne porte déjà l'étiquette de son objet
    if (this.showObjectLink && this.context !== "object" && !this.enableDragDrop) {
      // Passe 1 : groupes depuis les tâches avec object_name
      for (const task of section.tasks) {
        if (this.lots.find((l) => l.task_ids.includes(task.task_id))) continue;
        const link = task.objects_links?.[0];
        if (!link?.object_name) continue;
        const key = lk(link.object_type, link.object_id);
        if (!objectGroupMap.has(key)) objectGroupMap.set(key, { link, items: [] });
        objectGroupMap.get(key)!.items.push({ kind: "task", task });
      }
      // Passe 2 : tâches sans object_name → rejoindre groupe par object_id
      for (const task of section.tasks) {
        if (this.lots.find((l) => l.task_ids.includes(task.task_id))) continue;
        const link = task.objects_links?.[0];
        if (!link || link.object_name) continue;
        const key = lk(link.object_type, link.object_id);
        const group = objectGroupMap.get(key);
        if (group) group.items.push({ kind: "task", task });
      }
      // Passe 3 : lots
      for (const lot of this.lots) {
        const lotTasks = getLotTasksOrdered(lot);
        if (lotTasks.length === 0) continue;
        const commonLink = getLotLink(lot);
        if (!commonLink) continue;
        const key = lk(commonLink.object_type, commonLink.object_id);
        if (!objectGroupMap.has(key)) objectGroupMap.set(key, { link: commonLink, items: [] });
        objectGroupMap.get(key)!.items.push({ kind: "lot", lot, tasks: lotTasks });
      }
      // Passe 4 : fusionner les groupes de même type+nom (IDs backend différents)
      const nameToKey = new Map<string, string>();
      for (const [key, group] of [...objectGroupMap.entries()]) {
        if (!group.link.object_name) continue;
        const nk = `${group.link.object_type?.toUpperCase()}::${group.link.object_name.toLowerCase()}`;
        if (!nameToKey.has(nk)) {
          nameToKey.set(nk, key);
        } else {
          objectGroupMap.get(nameToKey.get(nk)!)!.items.push(...group.items);
          objectGroupMap.delete(key);
        }
      }
    }

    const getSubItemDate = (item: GroupSubItem): number => {
      if (item.kind === "task") {
        const d = item.task.due_date || item.task.planned_start_date || item.task.actual_start_date;
        return d ? new Date(d).getTime() : Number.MAX_SAFE_INTEGER;
      }
      const dates = item.tasks
        .map((t) => t.due_date || t.planned_start_date || t.actual_start_date)
        .filter(Boolean)
        .map((d) => new Date(d).getTime());
      return dates.length ? Math.min(...dates) : Number.MAX_SAFE_INTEGER;
    };

    const activeGroups = new Map<string, { link: TaskObjectLink; items: GroupSubItem[] }>(
      [...objectGroupMap.entries()].filter(([, g]) => g.items.length >= 2),
    );
    for (const group of activeGroups.values()) {
      group.items.sort((a, b) => getSubItemDate(a) - getSubItemDate(b));
    }

    // Index task_id/lot_id → group key pour la boucle de rendu
    const taskIdToGroupKey = new Map<string, string>();
    const lotIdToGroupKey = new Map<string, string>();
    for (const [key, group] of activeGroups.entries()) {
      for (const sub of group.items) {
        if (sub.kind === "task") taskIdToGroupKey.set(sub.task.task_id, key);
        else {
          lotIdToGroupKey.set(sub.lot.lot_id, key);
          sub.tasks.forEach((t) => taskIdToGroupKey.set(t.task_id, key));
        }
      }
    }

    const renderedGroupKeys = new Set<string>();

    const renderGroup = (groupKey: string) => {
      const group = activeGroups.get(groupKey)!;
      items.push({ kind: "object-group", link: group.link, items: group.items });
      for (const sub of group.items) {
        if (sub.kind === "task") renderedTaskIds.add(sub.task.task_id);
        else {
          renderedLotIds.add(sub.lot.lot_id);
          sub.tasks.forEach((t) => renderedTaskIds.add(t.task_id));
        }
      }
      renderedGroupKeys.add(groupKey);
    };

    // Construire maps pour lookup rapide
    const taskMap = new Map(section.tasks.map((t) => [t.task_id, t]));
    const lotMap = new Map(this.lots.map((l) => [l.lot_id, l]));
    const taskInLot = new Set(this.lots.flatMap((l) => l.task_ids));

    // === ORDRE MIXTE : si sectionOrder est peuplé, respecter l'ordre global ===
    if (this.hasManualOrder && section.key !== "done" && this.sectionOrder.length > 0) {
      for (const item of this.sectionOrder) {
        if (item.type === "lot") {
          const lot = lotMap.get(item.id);
          if (!lot || renderedLotIds.has(lot.lot_id)) continue;
          const lotTasks = getLotTasksOrdered(lot);
          items.push({ kind: "lot", lot, tasks: lotTasks });
          renderedLotIds.add(lot.lot_id);
          lotTasks.forEach((t) => renderedTaskIds.add(t.task_id));
        } else {
          const task = taskMap.get(item.id);
          if (!task || renderedTaskIds.has(task.task_id) || taskInLot.has(task.task_id)) continue;
          items.push({ kind: "task", task });
          renderedTaskIds.add(task.task_id);
        }
      }
      // Fallback : lots et tâches non encore référencés dans sectionOrder
      for (const lot of this.lots) {
        if (renderedLotIds.has(lot.lot_id)) continue;
        const lotTasks = getLotTasksOrdered(lot);
        items.push({ kind: "lot", lot, tasks: lotTasks });
        renderedLotIds.add(lot.lot_id);
        lotTasks.forEach((t) => renderedTaskIds.add(t.task_id));
      }
      for (const task of section.tasks) {
        if (renderedTaskIds.has(task.task_id) || taskInLot.has(task.task_id)) continue;
        items.push({ kind: "task", task });
        renderedTaskIds.add(task.task_id);
      }
      return items;
    }

    // === ORDRE PAR DÉFAUT : lots en premier, puis tâches standalone ===
    for (const lot of this.lots) {
      if (renderedLotIds.has(lot.lot_id)) continue;
      const groupKey = lotIdToGroupKey.get(lot.lot_id);
      if (groupKey) {
        if (!renderedGroupKeys.has(groupKey)) renderGroup(groupKey);
        continue;
      }
      const lotTasks = getLotTasksOrdered(lot);
      items.push({ kind: "lot", lot, tasks: lotTasks });
      renderedLotIds.add(lot.lot_id);
      lotTasks.forEach((t) => renderedTaskIds.add(t.task_id));
    }

    // Puis les tâches standalone et groupes d'objets
    for (const task of section.tasks) {
      if (renderedTaskIds.has(task.task_id)) continue;
      const groupKey = taskIdToGroupKey.get(task.task_id);
      if (groupKey && !renderedGroupKeys.has(groupKey)) {
        renderGroup(groupKey);
        continue;
      }
      if (!renderedTaskIds.has(task.task_id)) {
        items.push({ kind: "task", task });
        renderedTaskIds.add(task.task_id);
      }
    }

    return items;
  }

  trackByDisplayItem(_index: number, item: SectionDisplayItem): string {
    if (item.kind === "task") return item.task.task_id;
    if (item.kind === "lot") return item.lot.lot_id;
    return `group:${item.link.object_type}:${item.link.object_id}`;
  }

  isSectionLot(item: SectionDisplayItem): item is { kind: "lot"; lot: TaskLot; tasks: Task[] } {
    return item.kind === "lot";
  }

  isObjectGroup(
    item: SectionDisplayItem,
  ): item is { kind: "object-group"; link: TaskObjectLink; items: GroupSubItem[] } {
    return item.kind === "object-group";
  }

  isGroupSubLot(item: GroupSubItem): item is { kind: "lot"; lot: TaskLot; tasks: Task[] } {
    return item.kind === "lot";
  }

  /** Tag d'en-tête de section, sur le modèle des sections des notes (Épinglé, Autres) */
  sectionPreset(section: TaskSection): TagPreset {
    const icons: Record<string, Pick<TagPreset, "severity" | "icon">> = {
      overdue: { severity: "danger", icon: "heroExclamationCircle" },
      today: { severity: "info", icon: "heroCalendar" },
      later: { severity: "secondary", icon: "heroClock" },
      active: { severity: "secondary", icon: "heroListBullet" },
      done: { severity: "success", icon: "heroCheck" },
    };
    return { label: section.title, ...(icons[section.key] ?? { severity: "secondary", icon: "heroListBullet" }) };
  }

  getObjectGroupPreset(link: TaskObjectLink): TagPreset {
    const evaKey = OBJECT_TYPE_TO_EVA[link.object_type?.toUpperCase()] ?? link.object_type;
    const base = EVA_OBJECT_TAGS[evaKey] ?? { label: link.object_type, severity: "secondary" as const };
    return { ...base, label: link.object_name || base.label };
  }

  openInTasksPage(): void {
    if (this.objectFilter) {
      this.router.navigate(["/tasks"], {
        queryParams: {
          object_id: this.objectFilter.object_id,
          object_type: this.objectFilter.object_type,
        },
      });
    } else {
      this.router.navigate(["/tasks"]);
    }
  }

  // ==========================================================================
  // Drag & drop
  // ==========================================================================

  onDrop(event: CdkDragDrop<any[]>, targetSection: TaskSection): void {
    if (event.previousContainer === event.container) {
      const droppedData = event.item.data;

      // === MODE MIXTE (all-flat, object) : réordonnancement global lots + tâches ===
      if (this.hasManualOrder && targetSection.key !== "done") {
        if (event.previousIndex === event.currentIndex) return;
        const currentItems = this.getSectionDisplayItems(targetSection);
        moveItemInArray(currentItems, event.previousIndex, event.currentIndex);
        this.sectionOrder = this.computeSectionOrderFromItems(currentItems);

        // Si un lot a été déplacé, mettre à jour this.lots + persister lot.order
        if (droppedData && typeof droppedData === "object" && "lot_id" in droppedData) {
          const movedLot = droppedData as TaskLot;
          const newLotOrder = this.sectionOrder.filter((i) => i.type === "lot").map((i) => i.id);
          const newLots = newLotOrder
            .map((id) => this.lots.find((l) => l.lot_id === id))
            .filter((l): l is TaskLot => !!l);
          this.lots = newLots;
          if (this.objectFilter?.object_id) {
            this.taskLotService
              .reorderLots(
                this.objectFilter.object_id,
                newLots.map((l) => l.lot_id),
              )
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe();
          }
          void movedLot;
        }

        this.buildObjectSections(this.rawTasks);
        this.cdr.markForCheck();
        this.saveSectionOrder();
        return;
      }

      // === MODE CLASSIQUE : lot reorder ===
      if (droppedData && typeof droppedData === "object" && "lot_id" in droppedData) {
        const lot = droppedData as TaskLot;
        const fromIdx = this.lots.findIndex((l) => l.lot_id === lot.lot_id);
        if (fromIdx === -1) return;
        const toIdx = Math.max(0, Math.min(event.currentIndex, this.lots.length - 1));
        if (fromIdx === toIdx) return;
        const newLots = [...this.lots];
        moveItemInArray(newLots, fromIdx, toIdx);
        this.lots = newLots;
        this.buildObjectSections(this.rawTasks);
        this.cdr.markForCheck();
        if (this.objectFilter?.object_id) {
          this.taskLotService
            .reorderLots(
              this.objectFilter.object_id,
              newLots.map((l) => l.lot_id),
            )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe();
        }
        return;
      }
      moveItemInArray(targetSection.tasks, event.previousIndex, event.currentIndex);
      this.saveUserOrder(targetSection);
      this.cdr.markForCheck();
      return;
    }

    const task: Task = event.item.data ?? event.previousContainer.data[event.previousIndex];
    if (!task) return;

    // Tâche venant d'un lot → la retirer du lot (et l'insérer dans l'ordre global)
    const sourceId = event.previousContainer.id;
    if (sourceId.startsWith("lot-drop-")) {
      const lotId = sourceId.replace("lot-drop-", "");

      // SYNCHRONE : calculer et persister sectionOrder immédiatement, avant tout appel async
      if (this.hasManualOrder) {
        const newOrder = this.sectionOrder.filter((i) => !(i.type === "task" && i.id === task.task_id));
        // S'assurer que tous les lots sont présents
        const lotsInOrder = new Set(newOrder.filter((i) => i.type === "lot").map((i) => i.id));
        for (const lot of this.lots) {
          if (!lotsInOrder.has(lot.lot_id)) {
            newOrder.push({ type: "lot" as const, id: lot.lot_id });
          }
        }
        newOrder.splice(Math.min(event.currentIndex, newOrder.length), 0, {
          type: "task" as const,
          id: task.task_id,
        });
        this.sectionOrder = newOrder;
        this.saveSectionOrder(); // persisté AVANT l'appel removeTaskFromLot
      }

      this.taskLotService
        .removeTaskFromLot(lotId, task.task_id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (updatedLot) => {
            this.lots = this.lots.map((l) => (l.lot_id === updatedLot.lot_id ? updatedLot : l));
            if (this.context === "object") {
              this.buildObjectSections(this.rawTasks);
            } else {
              this.applyFilterAndSort();
            }
            this.cdr.markForCheck();
          },
          error: () => this.loadTasks(),
        });
      return;
    }

    const today = this.getStartOfDay(new Date());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    let newDate: Date | null = null;
    switch (targetSection.key) {
      case "today":
        newDate = today;
        break;
      case "later":
        // Déplacer dans "Plus tard" → effacer la date
        newDate = null;
        break;
    }

    // Mise à jour optimiste
    const sourceSection = this.sections.find((s) => s.dropListId === event.previousContainer.id);
    if (sourceSection) {
      sourceSection.tasks.splice(event.previousIndex, 1);
    }
    task.due_date = newDate;
    targetSection.tasks.splice(event.currentIndex, 0, task);
    this.saveUserOrder(targetSection);

    // Persistance API
    this.taskService
      .updateTask(task.task_id, { due_date: newDate })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.evaService.showSuccess("Tâche déplacée");
        },
        error: () => {
          this.evaService.showError("Impossible de déplacer la tâche", "updateTask", "");
          this.loadTasks(); // Rollback
        },
      });

    this.cdr.markForCheck();
  }

  // ==========================================================================

  // ==========================================================================
  // Helpers
  // ==========================================================================

  private getStartOfDay(date: Date): Date {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  toggleSection(section: TaskSection): void {
    section.collapsed = !section.collapsed;
    this.cdr.markForCheck();
  }

  trackByTaskId(index: number, task: Task): string {
    return task.task_id;
  }

  getLotColor(lot: TaskLot): string {
    const palette = [
      "var(--green-500)",
      "var(--yellow-500)",
      "var(--orange-500)",
      "var(--red-400)",
      "var(--purple-500)",
      "var(--cyan-500)",
      "var(--teal-500)",
      "var(--blue-500)",
    ];
    const idx = this.lots.findIndex((l) => l.lot_id === lot.lot_id);
    return palette[idx % palette.length] ?? palette[0];
  }
}
